import axios from "axios";
import { toast } from "react-toastify";
import { getUserToken } from "./helpers/authHelper";
import { handleAuthFailure, isAuthRedirecting } from "./helpers/authRedirect";

const TOKEN_ERRORS = ["jwt expired", "jwt malformed", "Token missing"];

// ─── Shared auth error handler ───────────────────────────────────────────────
const authErrorHandler = (error) => {
  if (
    axios.isCancel(error) ||
    error.name === "CanceledError" ||
    error.name === "AbortError"
  ) {
    return new Promise(() => {});
  }

  if (isAuthRedirecting()) {
    return new Promise(() => {});
  }

  const err = error?.response?.data;
  const status = error?.response?.status;
  const isTokenError =
    status === 401 ||
    TOKEN_ERRORS.includes(err?.error);

  const isAccessDenied = status === 403 && !isTokenError;

  if (isTokenError) {
    handleAuthFailure();
    return new Promise(() => {});
  }

  if (isAccessDenied) {
    toast.error(err?.message || "Access denied.");
    return Promise.reject(error);
  }

  return Promise.reject(error);
};

// ─── Global axios interceptor ─────────────────────────────────────────────────
// Covers all raw `axios.get/post/...` calls in Header, Sidebar, etc.
axios.interceptors.response.use((response) => response, authErrorHandler);

// ─── App-level axios instance ────────────────────────────────────────────────
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "",
});

api.interceptors.request.use((config) => {
  const token = getUserToken();

  if (token && token.startsWith("Bearer ")) {
    config.headers.Authorization = token;
  }

  config.headers["X-DomainID"] = localStorage.getItem("subdomainId") || "";
  const rawLangId = localStorage.getItem("lang_id");
  const sanitizedLangId = parseInt(rawLangId) || 1;
  config.headers["X-Lang_id"] = sanitizedLangId;
  if (sanitizedLangId !== Number(rawLangId)) {
    localStorage.setItem("lang_id", sanitizedLangId);
  }

  return config;
});

api.interceptors.response.use((response) => response, authErrorHandler);

export default api;
